import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, ChevronDown, ChevronUp, FileText, Pencil } from "lucide-react";

export default function CourseEdit() {
    const { id } = useParams();
    const [course, setCourse] = useState(null);
    const [moduleTitle, setModuleTitle] = useState("");
    const [lessonOpen, setLessonOpen] = useState(null); // module id
    const [lessonForm, setLessonForm] = useState({ title: "", youtube_url: "", notes: "", duration_seconds: 600, is_mandatory: true });
    const [expanded, setExpanded] = useState({});
    const [previewLesson, setPreviewLesson] = useState(null);
    const [previewNotes, setPreviewNotes] = useState("");
    const [savingNotes, setSavingNotes] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({});

    const load = async () => { const { data } = await api.get(`/courses/${id}`); setCourse(data); };
    useEffect(() => { load(); }, [id]); // eslint-disable-line

    const addModule = async () => {
        if (!moduleTitle.trim()) return;
        await api.post(`/courses/${id}/modules`, { title: moduleTitle });
        setModuleTitle("");
        toast.success("Module added");
        load();
    };

    const addLesson = async (modId) => {
        await api.post(`/modules/${modId}/lessons`, lessonForm);
        setLessonOpen(null);
        setLessonForm({ title: "", youtube_url: "", notes: "", duration_seconds: 600, is_mandatory: true });
        toast.success("Lesson added");
        load();
    };

    const deleteModule = async (modId) => {
        if (!window.confirm("Delete this module and all its lessons?")) return;
        await api.delete(`/modules/${modId}`);
        toast.success("Module deleted");
        load();
    };

    const deleteLesson = async (lid) => {
        if (!window.confirm("Delete this lesson?")) return;
        await api.delete(`/lessons/${lid}`);
        toast.success("Lesson deleted");
        load();
    };

    const openEditCourse = () => {
        setEditForm({ title: course.title, description: course.description || "", level: course.level || "", category: course.category || "", thumbnail_url: course.thumbnail_url || "" });
        setEditOpen(true);
    };

    const saveCourse = async () => {
        await api.put(`/courses/${id}`, editForm);
        toast.success("Course updated");
        setEditOpen(false);
        load();
    };

    const openPreview = (l) => { setPreviewLesson(l); setPreviewNotes(l.notes || ""); };

    const saveNotes = async () => {
        setSavingNotes(true);
        await api.put(`/lessons/${previewLesson.id}`, { notes: previewNotes });
        toast.success("Notes saved");
        setSavingNotes(false);
        setPreviewLesson(null);
        load();
    };

    const togglePublish = async () => {
        const newStatus = course.status === "PUBLISHED" ? "DRAFT" : "PUBLISHED";
        await api.put(`/courses/${id}`, { status: newStatus });
        toast.success(`Course ${newStatus}`);
        load();
    };

    if (!course) return null;

    return (
        <div className="space-y-6" data-testid="course-edit-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div className="flex-1">
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{course.category}</div>
                    <div className="flex items-center gap-3 mt-1">
                        <h1 className="font-display text-3xl lg:text-4xl">{course.title}</h1>
                        <Button variant="ghost" size="icon" onClick={openEditCourse} title="Edit course details"><Pencil className="h-4 w-4 text-muted-foreground" /></Button>
                    </div>
                    <p className="text-muted-foreground mt-2 max-w-2xl">{course.description}</p>
                    {course.thumbnail_url && <img src={course.thumbnail_url} alt="" className="mt-3 h-24 rounded-xl object-cover" />}
                </div>
                <Button onClick={togglePublish} className={course.status === "PUBLISHED" ? "bg-[#16A34A] hover:bg-[#0E8A3D]" : "mx-btn-primary"} data-testid="toggle-publish">
                    {course.status === "PUBLISHED" ? "Published — Unpublish" : "Publish course"}
                </Button>
            </div>

            <Card className="mx-card">
                <CardContent className="p-5 flex gap-3">
                    <Input value={moduleTitle} onChange={e => setModuleTitle(e.target.value)} placeholder="New module title" data-testid="module-title-input" />
                    <Button onClick={addModule} className="mx-btn-primary" data-testid="add-module-btn"><Plus className="h-4 w-4 mr-2" /> Add module</Button>
                </CardContent>
            </Card>

            <div className="space-y-4">
                {course.modules.map((m, idx) => {
                    const isOpen = expanded[m.id] ?? true;
                    return (
                        <Card key={m.id} className="mx-card" data-testid={`module-${m.id}`}>
                            <CardContent className="p-0">
                                <div className="p-5 flex items-center gap-3 border-b border-border">
                                    <div className="h-10 w-10 rounded-xl bg-brand-gradient text-white flex items-center justify-center font-display">{idx + 1}</div>
                                    <div className="flex-1">
                                        <div className="font-bold text-base">{m.title}</div>
                                        <div className="text-xs text-muted-foreground">{m.lessons.length} lesson(s)</div>
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => setExpanded({ ...expanded, [m.id]: !isOpen })}>
                                        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => deleteModule(m.id)} data-testid={`delete-module-${m.id}`}><Trash2 className="h-4 w-4 text-[#E11D48]" /></Button>
                                </div>
                                {isOpen && (
                                    <div className="p-5 space-y-2">
                                        {m.lessons.map((l, li) => (
                                            <div key={l.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/40 border border-border" data-testid={`lesson-${l.id}`}>
                                                <div className="h-8 w-8 rounded-lg bg-card border border-border flex items-center justify-center text-xs font-bold">{li + 1}</div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-semibold text-sm truncate">{l.title}</div>
                                                    <div className="text-xs text-muted-foreground truncate">{l.youtube_url}</div>
                                                </div>
                                                <Badge variant="outline" className="text-[10px]">{l.status}</Badge>
                                                {l.is_mandatory && <Badge className="bg-[#FFA81B] hover:bg-[#FFA81B] text-[10px]">Mandatory</Badge>}
                                                <Button variant="ghost" size="icon" onClick={() => openPreview(l)} title="View / edit notes"><FileText className="h-4 w-4 text-muted-foreground" /></Button>
                                                <Button variant="ghost" size="icon" onClick={() => deleteLesson(l.id)} data-testid={`delete-lesson-${l.id}`}><Trash2 className="h-4 w-4 text-[#E11D48]" /></Button>
                                            </div>
                                        ))}
                                        <Dialog open={lessonOpen === m.id} onOpenChange={(v) => setLessonOpen(v ? m.id : null)}>
                                            <DialogTrigger asChild>
                                                <Button variant="outline" className="w-full rounded-xl mt-2" data-testid={`add-lesson-${m.id}`}><Plus className="h-4 w-4 mr-2" /> Add lesson</Button>
                                            </DialogTrigger>
                                            <DialogContent>
                                                <DialogHeader><DialogTitle>New lesson</DialogTitle></DialogHeader>
                                                <div className="space-y-3">
                                                    <div><Label>Title</Label><Input value={lessonForm.title} onChange={e => setLessonForm({ ...lessonForm, title: e.target.value })} data-testid="lesson-title-input" /></div>
                                                    <div><Label>YouTube URL</Label><Input value={lessonForm.youtube_url} onChange={e => setLessonForm({ ...lessonForm, youtube_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" data-testid="lesson-yt-input" /></div>
                                                    <div><Label>Notes</Label><Textarea value={lessonForm.notes} onChange={e => setLessonForm({ ...lessonForm, notes: e.target.value })} data-testid="lesson-notes-input" /></div>
                                                    <div className="flex items-center gap-2">
                                                        <Switch checked={lessonForm.is_mandatory} onCheckedChange={(v) => setLessonForm({ ...lessonForm, is_mandatory: v })} />
                                                        <Label className="!mt-0">Mandatory for certificate</Label>
                                                    </div>
                                                </div>
                                                <DialogFooter><Button onClick={() => addLesson(m.id)} className="mx-btn-primary" data-testid="lesson-create-submit">Add lesson</Button></DialogFooter>
                                            </DialogContent>
                                        </Dialog>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
                {course.modules.length === 0 && <div className="text-sm text-muted-foreground text-center py-12 mx-card">No modules yet. Add the first one above.</div>}
            </div>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Edit course details</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Title</Label><Input value={editForm.title || ""} onChange={e => setEditForm({ ...editForm, title: e.target.value })} /></div>
                        <div><Label>Description</Label><Textarea value={editForm.description || ""} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={3} /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Level</Label><Input value={editForm.level || ""} onChange={e => setEditForm({ ...editForm, level: e.target.value })} /></div>
                            <div><Label>Category</Label><Input value={editForm.category || ""} onChange={e => setEditForm({ ...editForm, category: e.target.value })} /></div>
                        </div>
                        <div>
                            <Label>Thumbnail URL</Label>
                            <Input value={editForm.thumbnail_url || ""} onChange={e => setEditForm({ ...editForm, thumbnail_url: e.target.value })} placeholder="https://…" />
                            {editForm.thumbnail_url && <img src={editForm.thumbnail_url} alt="" className="mt-2 w-full rounded-xl object-cover aspect-video" />}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                        <Button onClick={saveCourse} className="mx-btn-primary">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!previewLesson} onOpenChange={(v) => { if (!v) setPreviewLesson(null); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle className="leading-snug">{previewLesson?.title}</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        <Label>Notes</Label>
                        <Textarea
                            value={previewNotes}
                            onChange={e => setPreviewNotes(e.target.value)}
                            rows={12}
                            className="text-sm font-mono resize-none"
                            placeholder="No notes yet."
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewLesson(null)}>Cancel</Button>
                        <Button onClick={saveNotes} disabled={savingNotes} className="mx-btn-primary">
                            {savingNotes ? "Saving…" : "Save notes"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
